import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { confirm } from "@/components/ConfirmDialog";
import { StickyNote, Pencil, Trash2, Plus, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

// Eski tekil defter notu (ledger_note) da liste olarak görünür; ilk düzenlemede sunucu listeye taşır.
export const notesOf = (c) => {
  if (Array.isArray(c?.notes)) return c.notes;
  const t = String(c?.ledger_note || "").trim();
  return t ? [{ id: "legacy", text: t, created_at: c.updated_at || c.created_at, created_by: "" }] : [];
};

export function NoteDialog({ open, contact, note, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setText(note?.text || ""); }, [open, note]);
  const save = async () => {
    if (!text.trim()) return toast.error("Not boş olamaz");
    setBusy(true);
    try {
      if (note) await api.put(`/contacts/${contact.id}/notes/${note.id}`, { text });
      else await api.post(`/contacts/${contact.id}/notes`, { text });
      toast.success(note ? "Not güncellendi" : "Not eklendi");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="note-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#0F2745] text-[#F2B705] flex items-center justify-center"><StickyNote className="h-4 w-4" /></span>
            {note ? "Notu Düzenle" : "Yeni Not"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">{contact?.title} · cari açıldığında en üstte görünür</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} autoFocus placeholder="Örn. Ödemeler her ayın 15'inde yapılır · Teslimat şantiyeye…" className="text-sm" data-testid="note-text" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="note-cancel">Vazgeç</Button>
          <Button onClick={save} disabled={busy} data-testid="note-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (note ? "Kaydet" : "Notu Ekle")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoteCard({ n, isAdmin, onEdit, onDelete }) {
  return (
    <article className="group relative rounded-lg bg-white border border-amber-200/80 shadow-sm pl-4 pr-3 py-3 animate-in fade-in slide-in-from-top-1 duration-300" data-testid={`note-${n.id}`}>
      <span className="absolute left-0 top-3 bottom-3 w-1 rounded-r bg-[#F2B705]" />
      <p className="text-sm font-medium text-slate-800 whitespace-pre-wrap break-words leading-relaxed" data-testid={`note-text-${n.id}`}>{n.text}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
        <span className="tabular-nums whitespace-nowrap">{fmtDateTime(n.created_at)}</span>
        {n.created_by && <span className="whitespace-nowrap">· {n.created_by}</span>}
        {n.updated_at && <span className="italic basis-full order-last">düzenlendi {fmtDateTime(n.updated_at)}{n.updated_by ? ` (${n.updated_by})` : ""}</span>}
        {isAdmin && (
          <span className="ml-auto flex items-center gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity">
            <button type="button" onClick={() => onEdit(n)} className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-slate-100 hover:text-[#0F2745]" title="Düzenle" data-testid={`note-edit-${n.id}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onDelete(n)} className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-red-50 hover:text-destructive" title="Sil" data-testid={`note-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        )}
      </div>
    </article>
  );
}

// Not varsa cari açılınca en üstte görünür; not yoksa hiç çizilmez.
export function ContactNotesBanner({ contact, notes, isAdmin, onAdd, onEdit, onChanged }) {
  const [all, setAll] = useState(false);
  if (!notes.length) return null;
  const shown = all ? notes : notes.slice(0, 3);
  const del = async (n) => {
    if (!(await confirm({ title: "Not silinsin mi?", description: "Bu not cariden kaldırılır.", confirmText: "Evet, Sil", destructive: true }))) return;
    try { await api.delete(`/contacts/${contact.id}/notes/${n.id}`); toast.success("Not silindi"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section className="shrink-0 rounded-xl border border-amber-300/70 bg-amber-50/70 shadow-sm" data-testid="contact-notes-banner">
      <header className="flex items-center gap-3 px-4 py-2.5 border-b border-amber-200/80">
        <span className="h-8 w-8 rounded-lg bg-[#0F2745] text-[#F2B705] flex items-center justify-center shrink-0"><StickyNote className="h-4 w-4" /></span>
        <div className="min-w-0">
          <h3 className="font-head text-xs font-black uppercase tracking-[0.18em] text-[#0F2745]">Cari Notları</h3>
          <p className="text-[11px] text-amber-800/80">{notes.length} not · en yeni en üstte</p>
        </div>
        {isAdmin && <Button size="sm" variant="outline" className="ml-auto h-8 border-[#0F2745]/30 text-[#0F2745] bg-white hover:bg-[#0F2745] hover:text-white" onClick={onAdd} data-testid="notes-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Not Ekle</Button>}
      </header>
      <div className="p-3 grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((n) => <NoteCard key={n.id} n={n} isAdmin={isAdmin} onEdit={onEdit} onDelete={del} />)}
      </div>
      {notes.length > 3 && (
        <button type="button" onClick={() => setAll((v) => !v)} className="w-full py-1.5 rounded-b-xl text-xs font-semibold text-amber-900 hover:bg-amber-100/70 border-t border-amber-200/80 inline-flex items-center justify-center gap-1" data-testid="notes-toggle-all">
          {all ? <><ChevronUp className="h-3.5 w-3.5" />Daha az göster</> : <><ChevronDown className="h-3.5 w-3.5" />Tüm notları göster ({notes.length})</>}
        </button>
      )}
    </section>
  );
}
